/**
 * Secure fetch wrapper: uses REACT_APP_API_URL and attaches Bearer token from Clerk getToken.
 * @param {string} path - API path (e.g. '/api/auth/verify')
 * @param {Object} options - fetch options; getToken (from useAuth()) is used to add Authorization when provided
 * @returns {Promise<Response>}
 */
export async function api(path, options = {}) {
  const { getToken, ...fetchOptions } = options;
  
  // Get base URL from environment variable
  // If empty or not set, use relative URLs (same origin) - this works when frontend and API are on same domain
  let baseUrl = process.env.REACT_APP_API_URL || '';
  
  // Clean up the base URL - remove trailing slashes
  baseUrl = baseUrl.trim().replace(/\/+$/, '');
  
  // If baseUrl is empty, use relative URLs (same origin)
  // Otherwise, ensure it's a valid absolute URL
  let url;
  if (!baseUrl) {
    // Use relative URL (same origin) - frontend and API are on same domain
    url = path.startsWith('/') ? path : `/${path}`;
  } else {
    // Use absolute URL (for dev or if explicitly configured)
    // If baseUrl doesn't start with http:// or https://, add http://
    if (!baseUrl.match(/^https?:\/\//)) {
      baseUrl = `http://${baseUrl}`;
    }
    url = path.startsWith('http') 
      ? path 
      : `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
  }

  const headers = new Headers(fetchOptions.headers);

  if (getToken && typeof getToken === 'function') {
    try {
      const token = await getToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      } else {
        // Token is null/undefined - this is expected for unauthenticated requests
        // but if this is an authenticated endpoint, it will return 401
        console.debug('api: getToken() returned null/undefined for', path);
      }
    } catch (err) {
      console.error('api: Error getting token:', err);
      // Continue without token - endpoint will return 401 if auth is required
    }
  }

  const response = await fetch(url, {
    ...fetchOptions,
    headers,
  });
  
  // 401s are expected when user is not authenticated - return response without logging
  return response;
}
